import type {
	DocumentToProcess,
	MissingDocument,
	NonCompliantDocument,
} from "@dune/types";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Chip,
} from "@dune/ui";
import {
	useGetReceptionSupportingDocumentsAnalysis,
	useGetSupportingDocumentsAnalysis,
} from "../../../../api/queries/constructionSiteAnalysis.queries";
import { PreviewFileLink } from "../../../../component/common/files/PreviewFileLink";
import { AddDocumentButton } from "../../../../component/constructionSites/submission/AddDocumentButton";
import { TableCellWithTooltip } from "../../../../component/shared/TableCellWithTooltip";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { useLinkFileToConstructionSiteReception } from "../../../../api/mutations/constructionSiteReception.mutations";
import { useLinkFileToConstructionSiteSubmission } from "../../../../api/mutations/constructionSiteSubmission.mutations";

type SupportingDocumentsControlProps = {
	context: "submission" | "reception";
};

export const SupportingDocumentsControl = ({
	context,
}: SupportingDocumentsControlProps) => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const {
		data: submissionData,
		isLoading: isLoadingSubmission,
		isError: isErrorSubmission,
	} = useGetSupportingDocumentsAnalysis(
		Number(constructionSiteId),
		context === "submission",
	);
	const {
		data: receptionData,
		isLoading: isLoadingReception,
		isError: isErrorReception,
	} = useGetReceptionSupportingDocumentsAnalysis(
		Number(constructionSiteId),
		context === "reception",
	);

	const data = context === "submission" ? submissionData : receptionData;
	const isLoading =
		context === "submission" ? isLoadingSubmission : isLoadingReception;
	const isError =
		context === "submission" ? isErrorSubmission : isErrorReception;

	if (isLoading) {
		return (
			<div className="flex flex-col gap-2">
				<div className="text-sm text-gray-500">Chargement des documents...</div>
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div className="flex flex-col gap-2">
				<div className="text-sm text-gray-500">
					Impossible de charger l'analyse des pièces justificatives
				</div>
			</div>
		);
	}

	const missingDocuments = data.missingDocuments;
	const receptionDocumentsToProcess = data.documentsToProcess;
	const receptionNonCompliantDocuments = data.nonCompliantDocuments;

	return (
		<div className="flex flex-col gap-8">
			{missingDocuments.length > 0 && (
				<div>
					<h4 className="text-sm font-medium text-[#344054] mb-4">
						Documents manquants ({missingDocuments.length})
					</h4>
					<DocumentsTableMissing
						rows={missingDocuments}
						constructionSiteId={Number(constructionSiteId)}
						context={context}
					/>
				</div>
			)}

			{receptionDocumentsToProcess.length > 0 && (
				<div>
					<h4 className="text-sm font-medium text-[#344054] mb-4">
						Documents à traiter ({receptionDocumentsToProcess.length})
					</h4>
					<DocumentsTableToProcess rows={receptionDocumentsToProcess} />
				</div>
			)}

			{receptionNonCompliantDocuments.length > 0 && (
				<div>
					<h4 className="text-sm font-medium text-[#344054] mb-4">
						Documents non conformes ({receptionNonCompliantDocuments.length})
					</h4>
					<DocumentsTableNonCompliant rows={receptionNonCompliantDocuments} />
				</div>
			)}
		</div>
	);
};

const DocumentsTableMissing = ({
	rows,
	constructionSiteId,
	context,
}: {
	rows: MissingDocument[];
	constructionSiteId: number;
	context: SupportingDocumentsControlProps["context"];
}) => {
	const linkFileSubmissionMutation =
		useLinkFileToConstructionSiteSubmission(constructionSiteId);
	const linkFileReceptionMutation =
		useLinkFileToConstructionSiteReception(constructionSiteId);

	return (
		<div className="bg-white rounded-lg border">
			<Table className="table-fixed w-full">
				<TableHeader>
					<TableRow>
						<TableHead>Type</TableHead>
						<TableHead>Participants</TableHead>
						<TableHead>Documents</TableHead>
						<TableHead>Commentaire</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row, idx) => (
						<TableRow key={`${row.documentType}-${idx}`}>
							<TableCellWithTooltip
								className="align-middle"
								tooltipContent={row.typeLabel}
							>
								{row.typeLabel}
							</TableCellWithTooltip>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									{row.participants.map(({ name }) => (
										<span key={`${row.documentType}-${name}`}>{name}</span>
									))}
								</div>
							</TableCell>
							<TableCell className="align-middle">
								<div className="flex flex-col gap-2">
									{row.documents ? <span>{row.documents}</span> : null}
									{row.participants.map(({ id: participantId, name }) => {
										return (
											<div key={`${row.documentType}-${name}-add`}>
												<AddDocumentButton
													constructionSiteId={constructionSiteId}
													onUploadSuccess={(fileId) => {
														const docType = row.documentType;
														if (context === "submission") {
															linkFileSubmissionMutation.mutate({
																fileId,
																type: docType,
																participantId: participantId ?? undefined,
															});
														} else {
															linkFileReceptionMutation.mutate({
																fileId,
																type: docType,
																participantId: participantId ?? undefined,
															});
														}
													}}
												/>
											</div>
										);
									})}
								</div>
							</TableCell>
							<TableCell className="align-middle">
								{row.comment ?? "—"}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};

const DocumentsTableToProcess = ({ rows }: { rows: DocumentToProcess[] }) => {
	return (
		<div className="bg-white rounded-lg border">
			<Table className="table-fixed w-full">
				<TableHeader>
					<TableRow>
						<TableHead>Statut</TableHead>
						<TableHead>Type</TableHead>
						<TableHead>Participant</TableHead>
						<TableHead>Documents</TableHead>
						<TableHead>Commentaire</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row, idx) => (
						<TableRow key={`${row.documentType}-${idx}`}>
							<TableCell className="align-middle">
								<Chip label="À traiter" color="orange" />
							</TableCell>
							<TableCellWithTooltip
								className="align-middle"
								tooltipContent={row.typeLabel}
							>
								{row.typeLabel}
							</TableCellWithTooltip>
							<TableCellWithTooltip
								className="align-middle"
								tooltipContent={row.participant?.name}
							>
								{row.participant?.name ?? "—"}
							</TableCellWithTooltip>
							<TableCell className="align-middle">
								{row.fileId ? (
									<PreviewFileLink
										file={{ id: row.fileId, name: row.fileName }}
									/>
								) : (
									<span>{row.fileName || "—"}</span>
								)}
							</TableCell>
							<TableCell className="align-middle">
								{row.comment ?? "—"}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};

const DocumentsTableNonCompliant = ({
	rows,
}: { rows: Array<NonCompliantDocument> }) => {
	return (
		<div className="bg-white rounded-lg border">
			<Table className="table-fixed w-full">
				<TableHeader>
					<TableRow>
						<TableHead>Statut</TableHead>
						<TableHead>Type</TableHead>
						<TableHead>Participant</TableHead>
						<TableHead>Documents</TableHead>
						<TableHead>Commentaire</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row, idx) => (
						<TableRow key={`${row.documentType}-${idx}`}>
							<TableCell className="align-middle">
								<Chip label="Non conforme" color="red" />
							</TableCell>
							<TableCellWithTooltip
								className="align-middle"
								tooltipContent={row.typeLabel}
							>
								{row.typeLabel}
							</TableCellWithTooltip>
							<TableCellWithTooltip
								className="align-middle"
								tooltipContent={row.participant?.name}
							>
								{row.participant?.name ?? "—"}
							</TableCellWithTooltip>
							<TableCell className="align-middle">
								{row.fileId ? (
									<PreviewFileLink
										file={{ id: row.fileId, name: row.fileName }}
									/>
								) : (
									<span>{row.fileName || "—"}</span>
								)}
							</TableCell>
							<TableCell className="align-middle">{row.comment}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};
